import React, { useState } from "react";
import { IconButton, Spinner } from "@material-tailwind/react";
import { Edit2Icon, EyeIcon } from "lucide-react";
import moment from "moment";
import { useGetUsers } from "../../custom-hooks/use-users";
import UserFormModal from "./userFormModal";
import { STATIC_USER_ROLES } from "../../utils/enums";
import { Link } from "react-router-dom";
import { PageHeader } from "../../components";

const INITIAL_FORM_DATA = {
  _id: '',
  email: '',
  password: '',
  name: '',
  role: 'user',
  cnic: '',
  phone: ''
};

const UserPage = () => {
  // State variables
  const [modalOpen, setModalOpen] = useState(false);
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [isUpdating, setIsUpdating] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);

  // Custom hooks
  const {
    data: list,
    isLoading: isUsersLoading,
    // error: usersError 
  } = useGetUsers({
    role: [STATIC_USER_ROLES.ADMIN, STATIC_USER_ROLES.MANAGER, STATIC_USER_ROLES.EMPLOYEE, STATIC_USER_ROLES.RECEPTIONIST],
    _limit: 1000
  });

  // Open modal for adding a new user
  const openAddUserModal = () => {
    setModalOpen(true);
    setFormData(INITIAL_FORM_DATA);
    setIsUpdating(false);
  };

  // Open modal for updating a user
  const openUpdateUserModal = (user) => {
    setModalOpen(true);
    setFormData({
      ...user,
      password: '' // Clear password field for security
    });
    setIsUpdating(true);
    setSelectedUserId(user._id);
  };

  return (
    <div>
      <div className="m-5 p-5 border rounded-3xl space-y-4">
        <PageHeader
          title="User Management"
          isLoading={isUsersLoading}
          actionOnClick={openAddUserModal}
          actionType="add"
          actionText="Add New User"
        />
        {
          isUsersLoading ?
            <div className="flex justify-center">
              <Spinner />
            </div> :
            <div className="overflow-x-auto ">
              <table className="w-full min-w-max table-auto text-left">
                <thead className="text-sm">
                  <tr className="py-3">
                    <th colSpan={6} className="py-3 text-xs font-light text-orange-900">
                      List of All Users ({list?.users.length})
                    </th>
                  </tr>
                  <tr className="py-3 uppercase">
                    <th className="py-3">Name</th>
                    <th className="py-3">Email</th>
                    <th className="py-3">CNIC</th>
                    <th className="py-3">Phone</th>
                    <th className="py-3">Role</th>
                    <th className="py-3">Created Date</th>
                    <th className="py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {list?.users.map((user, index) => (
                    <tr key={index} className="border-t hover:bg-gray-50 button rounded-full">
                      <td className="font-thin py-3 text-sm">{user.name}</td>
                      <td className="font-thin py-3 text-sm">{user.email}</td>
                      <td className="font-thin py-3 text-sm">{user.cnic}</td>
                      <td className="font-thin py-3 text-sm">{user.phone}</td>
                      <td className="font-thin py-3 text-sm capitalize">{user.role}</td>
                      <td className="font-thin py-3 text-sm">{moment(user.createdDate).format('lll')}</td>
                      <td className="py-3">
                        <div className="flex gap-1">
                          <Link to={`/user/${user._id}`}>
                            <IconButton
                              size="sm"
                              color="blue"
                              className="rounded-full"
                            >
                              <EyeIcon size={12} />
                            </IconButton>
                          </Link>
                          <IconButton
                            size="sm"
                            className="rounded-full"
                            onClick={() => openUpdateUserModal(user)}
                          >
                            <Edit2Icon size={12} />
                          </IconButton>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
        }
      </div>
      {/* User Form Modal */}
      {
        modalOpen &&
        <UserFormModal
          isOpen={modalOpen}
          onClose={setModalOpen}
          defaultData={formData}
          isUpdating={isUpdating}
          updatingUserId={selectedUserId}  // Passing selected user ID for updating purpose
        />
      }
    </div>
  );
};

export default UserPage;